import { useEffect, useState } from 'react'

import { Aborted, ApiError, api } from '../api/client'
import type { Ref } from '../components/common/Refs'
import type { RefDetail } from '../components/common/RefPopover'
import { geldtext, kapitalisierung } from '../pages/priceFormat'

/**
 * Lädt die Eckdaten zu einem Verweis — aus den BESTEHENDEN Endpunkten.
 *
 * KEIN EIGENER ENDPUNKT, sondern vier vorhandene, nach Bedarf: die
 * Stammdaten des Instruments, sein jüngster Kurs, die Ampel und die
 * Meldungen. Das kostet bis zu vier Aufrufe statt einem — dafür entsteht
 * keine fünfte Sicht auf dieselben Daten, die bei jeder Änderung mitgezogen
 * werden müsste, und jede Erweiterung nützt auch der Filterleiste.
 *
 * ERST BEIM AUFKLAPPEN. Eine Meldungsliste zeigt fünfzig Zeilen mit bis zu
 * drei Zielen; würde hier beim Rendern geladen, wären es hundertfünfzig
 * Abrufe für ein Popover, das niemand öffnet.
 *
 * JEDER ABSCHNITT SCHEITERT FÜR SICH. Die vier Endpunkte hängen an vier
 * verschiedenen Rechten — `instrument:read`, `quote:read`, `assessment:read`,
 * `news:read`. Wer eines nicht hat, bekommt für diesen Abschnitt eine 403,
 * und das Popover zeigt trotzdem, was es hat. Ein gemeinsames `Promise.all`
 * hätte am ersten Fehlschlag alles verloren.
 */

/** Wie viele Meldungen im Schnipsel stehen. Mehr macht es zur Liste. */
const NEWS_IM_SCHNIPSEL = 2

const ZEITPUNKT = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric',
})

/** Die Art in Worten, für den Kopf des Popovers. */
const ARTEN: Record<string, string> = {
  isin: 'Instrument',
  comm: 'Rohstoff',
  ind: 'Branche',
  geo: 'Land, Region oder Bündnis',
  crypt: 'Kryptowährung',
  mkt: 'Marktsegment',
}

/**
 * Wie lange etwas her ist, in Worten.
 *
 * Grob und mit Absicht: Im Schnipsel zählt „heute" gegen „vor drei Wochen",
 * nicht die Minute. Eine genaue Angabe stünde da und würde bei jedem Rendern
 * eine andere Zahl zeigen.
 */
function herStand(iso: string | null): string {
  if (!iso) return '—'
  const tage = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (tage <= 0) return 'heute'
  if (tage === 1) return 'gestern'
  if (tage < 31) return `vor ${tage} Tagen`
  return ZEITPUNKT.format(new Date(iso))
}

/**
 * Läuft ein Versprechen, ohne dass sein Fehlschlag die anderen mitnimmt.
 *
 * Ein 403 ist hier KEIN Fehler, sondern eine Auskunft: Dieser Abschnitt
 * bleibt leer, weil das Recht fehlt. Ein Abbruch beim Schließen ebenso.
 */
async function weich<T>(arbeit: Promise<T>): Promise<T | null> {
  try {
    return await arbeit
  } catch (cause) {
    if (cause instanceof Aborted) throw cause
    if (cause instanceof ApiError) return null
    return null
  }
}

export interface RefDetailState {
  detail: RefDetail
  loading: boolean
}

export function useRefDetail(verweis: Ref, offen: boolean): RefDetailState {
  // DER GRUNDRISS STEHT SOFORT, ohne einen einzigen Aufruf: Name, Art und
  // Verweis kommen aus dem Enricher und stecken schon in `targets`. Das
  // Popover ist damit vom ersten Bild an lesbar; nachgeladen wird nur, was
  // darüber hinausgeht.
  const grundriss: RefDetail = {
    ref: verweis.ref,
    kind: verweis.kind,
    name: verweis.name,
    art: verweis.kind ? ARTEN[verweis.kind] : undefined,
    schluessel: verweis.name === null
      ? undefined
      : { feld: verweis.kind === 'isin' ? 'ISIN' : 'Verweis',
          wert: verweis.kind === 'isin'
            ? verweis.ref.slice('isin:'.length) : verweis.ref },
    aktionen: sprunge(verweis),
  }

  // NUR DAS NACHGELADENE STEHT IM ZUSTAND, nicht das ganze `detail`.
  //
  // Läge der Grundriss mit darin, wäre er ab dem ersten Rendern eingefroren:
  // `useState` nimmt seinen Anfangswert nur beim Einhängen. Zeigte dieselbe
  // Stelle später auf ein anderes Ziel, stünde der alte Name da, bis jemand
  // aufklappt. Zusammengesetzt wird deshalb erst beim Zurückgeben.
  const [geladen, setGeladen] = useState<Partial<RefDetail> | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Ein Ziel, das es nicht mehr gibt, hat nichts nachzuladen — es gibt ja
    // nichts, worauf die Endpunkte antworten könnten.
    if (!offen || verweis.name === null) return
    let aktuell = true
    const steuerung = new AbortController()
    setLoading(true)

    async function laden() {
      const signal = steuerung.signal
      const istInstrument = verweis.kind === 'isin' && verweis.id !== null

      const [stamm, kurse, ampeln, meldungen] = await Promise.all([
        istInstrument
          ? weich(api.masterDataDetail(verweis.id!, {}, signal)) : null,
        istInstrument
          ? weich(api.quotes({ isin: [verweis.ref.slice('isin:'.length)],
                               sort: 'observed_at', dir: 'desc', size: 1 },
                             signal)) : null,
        weich(api.assessments({ ref: verweis.ref, sort: 'assessed_at',
                                dir: 'desc', size: 1 }, signal)),
        weich(api.news({ ref: [verweis.ref], sort: 'published_at',
                         dir: 'desc', size: NEWS_IM_SCHNIPSEL, total: true },
                       signal)),
      ])
      if (!aktuell) return

      const ampel = ampeln?.items?.[0]
      const kurs = kurse?.items?.[0]

      setGeladen({
        // In der Reihenfolge der Vorlage, nach dem Kurs: Kapitalisierung,
        // Boerse, Waehrung, Branche. Der Typ steht nicht mehr da — er war
        // fast immer „Aktie".
        daten: stamm ? [
          // Die Kapitalisierung mit Waehrung, wie ueberall — ohne sie waere
          // die Zahl irrefuehrend. Leer, wo die Stammdaten sie nicht haben.
          { feld: 'Marktkap.',
            wert: stamm.market_cap && stamm.market_cap_ccy
              ? kapitalisierung(stamm.market_cap, stamm.market_cap_ccy) : null },
          { feld: 'Börse', wert: stamm.primary_exchange },
          { feld: 'Währung', wert: kurs?.price.currency ?? null },
          { feld: 'Branche', wert: stamm.sector_name ?? null },
        ] : undefined,
        kurs: kurs
          // UEBER `geldtext` und nicht selbst zusammengesetzt: Der Betrag
          // kommt als "202.14" mit Punkt, und die Umrechnung in die
          // Zielwaehrung haengt mit dran. Beides steht dort schon.
          //
          // OHNE VERAENDERUNG: Die Prozentzahl steht in keinem Endpunkt und
          // muesste aus zwei Kursen gerechnet werden. Der Betrag allein
          // traegt das Schnipsel.
          // MIT DATUM: Ein Kurs von vorgestern saehe sonst aus wie einer
          // von heute.
          ? { betrag: `${geldtext(kurs.price)} · ${ZEITPUNKT.format(new Date(kurs.observed_at))}` }
          : undefined,
        ampel: ampel
          ? { stufe: ampel.rating, seit: herStand(ampel.assessed_at),
              satz: ampel.headline }
          : undefined,
        news: meldungen && meldungen.items.length > 0
          ? { anzahl: meldungen.total ?? meldungen.items.length,
              zuletzt: herStand(meldungen.items[0].published_at),
              items: meldungen.items.map((m) => ({
                id: m.id, sentiment: m.sentiment, headline: m.headline })) }
          : undefined,
      })
      setLoading(false)
    }

    laden().catch(() => { if (aktuell) setLoading(false) })
    return () => { aktuell = false; steuerung.abort() }
    // NUR an Verweis und Zustand, nicht an `grundriss`: Der entsteht bei
    // jedem Rendern neu, und als Abhängigkeit liefe die Wirkung endlos.
    // Alles, woraus er besteht, steht hier einzeln.
  }, [verweis.ref, verweis.id, verweis.name, verweis.kind, offen])

  return { detail: { ...grundriss, ...(geladen ?? {}) }, loading }
}

/**
 * Wohin man von einem Verweis aus springen kann.
 *
 * NUR DAS INSTRUMENT HAT EINE DETAILSEITE. Für die übrigen fünf Arten führt
 * der Sprung in die gefilterte Meldungsliste — das ist keine Notlösung,
 * sondern die ehrlichste Antwort auf „was gibt es dazu".
 */
function sprunge(verweis: Ref): { text: string; to: string }[] {
  if (verweis.name === null) return []
  const zielliste = `/news?ref=${encodeURIComponent(verweis.ref)}`
  if (verweis.kind === 'isin' && verweis.id !== null) {
    return [
      { text: 'Stammdaten', to: `/instruments/${verweis.id}` },
      { text: 'News', to: zielliste },
      // `von=-&bis=-` schuettelt die Vorbelegung „heute" der Ampel-Seite
      // ab (das Minus ist deren Konvention dafuer). Wer von einem Titel
      // aus springt, will seinen VERLAUF sehen — mit dem Fenster von heute
      // waere die Liste meist leer.
      { text: 'Ampel',
        to: `/assessments?ref=${encodeURIComponent(verweis.ref)}`
          + '&von=-&bis=-' },
    ]
  }
  return [{ text: 'Meldungen dazu', to: zielliste }]
}
